import { useState, useMemo } from "react";

export const useTableSort = (data) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  // VKD66 started
  const parseModelParameter = (param) => {
    if (!param || typeof param !== "string") return 0;

    const match = param.match(/([\d.]+)\s*([KMBT]?)/i); // K=thousand, M=million, B=billion, T=trillion
    if (!match) return 0;

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    const multiplier =
      {
        "": 1,
        K: 1e3,
        M: 1e6,
        B: 1e9,
        T: 1e12,
      }[unit] || 1;

    return value * multiplier;
  };
  // VKD66 End
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      // 🔹 Derived column: Text length
      if (sortConfig.key === "text_length") {
        valA = a.text_content?.length || 0;
        valB = b.text_content?.length || 0;

        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }

      if (sortConfig.key === "qa_length") {
        valA = (a.question?.length || 0) + (a.answer?.length || 0);

        valB = (b.question?.length || 0) + (b.answer?.length || 0);

        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }

      // String sort (null-safe)
      if (typeof valA === "string" || typeof valB === "string") {
        valA = valA ?? "";
        valB = valB ?? "";

        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      // bytes string → number
      if (sortConfig.key === "file_size") {
        valA = Number(valA || 0);
        valB = Number(valB || 0);

        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }

      // Convert dates
      if (isValidDate(valA) && isValidDate(valB)) {
        return sortConfig.direction === "asc"
          ? new Date(valA) - new Date(valB)
          : new Date(valB) - new Date(valA);
      }

      // Convert file sizes like "102.37 KB"
      if (typeof valA === "string" && valA.match(/kb|mb|gb/i)) {
        valA = convertSize(valA);
        valB = convertSize(valB);
      }

      // Alphabetical sort
      if (typeof valA === "string") {
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      // VKD66 started
      // Model parameters like "10B", "70B", "110B"
      if (sortConfig.key === "model_parameter") {
        valA = parseModelParameter(valA);
        valB = parseModelParameter(valB);

        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }
      // VKD66 End
      // Number sort
      return sortConfig.direction === "asc" ? valA - valB : valB - valA;
    });
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  return { sortedData, requestSort, sortConfig };
};

// 📌 Helpers
const isValidDate = (date) => !isNaN(new Date(date).getTime());

// const convertSize = (sizeStr) => {
//   const [value, unit] = sizeStr.split(" ");
//   const num = parseFloat(value);
//   const map = { KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
//   return num * (map[unit.toUpperCase()] || 1);
// };

const convertSize = (sizeStr) => {
  if (!sizeStr || typeof sizeStr !== "string") return 0;

  const match = sizeStr.match(/([\d.]+)\s*(kb|mb|gb)/i);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  const map = {
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
  };

  return value * map[unit];
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";

  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};
